import { useCallback, useEffect, useRef, useState } from 'react';

import s1_1200 from '@/assets/v2/slides/slide1-1200.webp';
import s1_2000 from '@/assets/v2/slides/slide1-2000.webp';
import s1_3200 from '@/assets/v2/slides/slide1-3200.webp';
import s1p1 from '@/assets/v2/slides/slide1-parte1.webp';
import s1p2 from '@/assets/v2/slides/slide1-parte2.webp';
import s1p3 from '@/assets/v2/slides/slide1-parte3.webp';
import s2_1200 from '@/assets/v2/slides/slide2-1200.webp';
import s2_2000 from '@/assets/v2/slides/slide2-2000.webp';
import s2_3200 from '@/assets/v2/slides/slide2-3200.webp';
import s2p1 from '@/assets/v2/slides/slide2-parte1.webp';
import s2p2 from '@/assets/v2/slides/slide2-parte2.webp';
import s2p3 from '@/assets/v2/slides/slide2-parte3.webp';
import s3_1200 from '@/assets/v2/slides/slide3-1200.webp';
import s3_2000 from '@/assets/v2/slides/slide3-2000.webp';
import s3_3200 from '@/assets/v2/slides/slide3-3200.webp';
import s3p1 from '@/assets/v2/slides/slide3-parte1.webp';
import s3p2 from '@/assets/v2/slides/slide3-parte2.webp';
import s3p3 from '@/assets/v2/slides/slide3-parte3.webp';

const INTERVALO = 5000;
const OCIOSO = 4000;
const CELULAR = '(max-width: 700px)';

/**
 * Os três painéis originais (4213x1369), em três larguras.
 *
 * Reencodados do PNG, não do webp já reduzido: a versão de 1600px que
 * estava aqui antes borrava a banda em qualquer tela retina, porque
 * uma tela de 1440 a 2x pede 2880px.
 */
const LARGOS = [
  {
    src: s1_2000,
    srcSet: `${s1_1200} 1200w, ${s1_2000} 2000w, ${s1_3200} 3200w`,
    alt: 'Colagem de fotos das participantes do Lily Book Club',
  },
  {
    src: s2_2000,
    srcSet: `${s2_1200} 1200w, ${s2_2000} 2000w, ${s2_3200} 3200w`,
    alt: 'Aqui, cada encontro é uma experiência: leitura do mês, encontro especial, uma comunidade feita para mulheres, brindes e dinâmicas, comidinhas e muito mais',
  },
  {
    src: s3_2000,
    srcSet: `${s3_1200} 1200w, ${s3_2000} 2000w, ${s3_3200} 3200w`,
    alt: 'Recortes de encontros do clube, com as participantes e os mimos da edição',
  },
];

/**
 * No celular são nove quadros, não três.
 *
 * Cada painel largo foi dividido em três pedaços de 1404x1369. Espremer
 * o painel inteiro num retrato de 400x520 cortava a frase impressa na
 * imagem e jogava fora dois terços da foto; em três quadros quase
 * quadrados nada se perde, e 1404px cobre com folga uma tela a 2x.
 */
const QUADROS = [
  { src: s1p1, alt: 'Participantes do Lily Book Club — parte 1 de 3' },
  { src: s1p2, alt: 'Participantes do Lily Book Club — parte 2 de 3' },
  { src: s1p3, alt: 'Participantes do Lily Book Club — parte 3 de 3' },
  { src: s2p1, alt: 'A experiência de um encontro do clube — parte 1 de 3' },
  { src: s2p2, alt: 'A experiência de um encontro do clube — parte 2 de 3' },
  { src: s2p3, alt: 'A experiência de um encontro do clube — parte 3 de 3' },
  { src: s3p1, alt: 'Recortes de encontros do clube — parte 1 de 3' },
  { src: s3p2, alt: 'Recortes de encontros do clube — parte 2 de 3' },
  { src: s3p3, alt: 'Recortes de encontros do clube — parte 3 de 3' },
];

export const HeroCarrossel = () => {
  const trilha = useRef(null);
  const relogio = useRef(null);
  const ocioso = useRef(null);
  const [atual, setAtual] = useState(0);
  const [noCelular, setNoCelular] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(CELULAR).matches,
  );

  const semMovimento =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // A quantidade de slides muda com a largura, não só a imagem, então
  // não dá para resolver com <picture>: é o React que troca a lista.
  useEffect(() => {
    const mq = window.matchMedia(CELULAR);
    const ouvir = (e) => {
      setNoCelular(e.matches);
      setAtual(0);
      if (trilha.current) trilha.current.scrollTo({ left: 0 });
    };
    mq.addEventListener('change', ouvir);
    return () => mq.removeEventListener('change', ouvir);
  }, []);

  const slides = noCelular ? QUADROS : LARGOS;

  /* Cinco é onde os tracinhos ainda cabem sem virar faixa. Acima disso
     eles deixam de informar e passam a parecer barra de rolagem. */
  const mostrarPontos = slides.length <= 5;

  const irPara = useCallback(
    (i) => {
      const el = trilha.current;
      if (!el) return;
      const total = slides.length;
      const alvo = ((i % total) + total) % total;
      el.scrollTo({
        left: el.clientWidth * alvo,
        behavior: semMovimento ? 'auto' : 'smooth',
      });
    },
    [slides.length, semMovimento],
  );

  const parar = useCallback(() => {
    if (relogio.current) {
      window.clearInterval(relogio.current);
      relogio.current = null;
    }
  }, []);

  // O autoplay lê o índice de um ref, não do estado: o setter do React
  // pode rodar duas vezes em StrictMode, e rolar a trilha lá dentro
  // faria o carrossel pular dois slides.
  const indice = useRef(0);
  useEffect(() => {
    indice.current = atual;
  }, [atual]);

  const rodar = useCallback(() => {
    if (semMovimento) return;
    parar();
    relogio.current = window.setInterval(
      () => irPara(indice.current + 1),
      INTERVALO,
    );
  }, [parar, semMovimento, irPara]);

  // Depois de um arraste não existe mouseleave no toque, então a
  // retomada vem de um tempo parado após o último scroll.
  const agendar = useCallback(() => {
    window.clearTimeout(ocioso.current);
    ocioso.current = window.setTimeout(rodar, OCIOSO);
  }, [rodar]);

  useEffect(() => {
    rodar();
    return () => {
      parar();
      window.clearTimeout(ocioso.current);
    };
  }, [rodar, parar]);

  // O slide no ar sai do observador, e não de um índice: assim o dedo
  // arrastando e o clique no pontinho nunca discordam.
  useEffect(() => {
    const el = trilha.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const quadros = el.querySelectorAll('[data-slide]');
    const obs = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.55) {
            setAtual(Number(e.target.getAttribute('data-slide')));
          }
        });
      },
      { root: el, threshold: [0.55] },
    );
    quadros.forEach((q) => obs.observe(q));
    return () => obs.disconnect();
  }, [slides]);

  const aoTeclar = (ev) => {
    if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft') return;
    ev.preventDefault();
    irPara(atual + (ev.key === 'ArrowRight' ? 1 : -1));
    parar();
    agendar();
  };

  return (
    <header className="relative">
      {/* O h1 sai da tela mas continua existindo: sem ele a home fica sem
          cabeçalho, e o texto dentro dos slides é pixel — nem leitor de
          tela nem buscador enxergam pixel. */}
      <h1 className="sr-only">
        Lily Book Club — aqui, cada encontro é uma experiência
      </h1>

      <div
        ref={trilha}
        tabIndex={0}
        role="group"
        aria-label="Fotos do clube, arraste para o lado"
        onMouseEnter={parar}
        onMouseLeave={rodar}
        onFocus={parar}
        onPointerDown={parar}
        onScroll={agendar}
        onKeyDown={aoTeclar}
        className="club-trilha flex w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden bg-surface-container-low focus-visible:outline focus-visible:-outline-offset-[3px] focus-visible:outline-[3px] focus-visible:outline-[var(--hue-fill)]"
      >
        {slides.map((slide, i) => (
          <figure
            key={slide.src}
            data-slide={i}
            className={`m-0 shrink-0 basis-full snap-start snap-always ${
              noCelular ? 'aspect-[1404/1369]' : 'aspect-[1600/520]'
            }`}
          >
            <img
              src={slide.src}
              srcSet={slide.srcSet}
              sizes="100vw"
              alt={slide.alt}
              loading="eager"
              fetchpriority={i === 0 ? 'high' : undefined}
              decoding="async"
              className="h-full w-full object-cover"
            />
          </figure>
        ))}
      </div>

      {/* Os controles passam a morar sobre a foto: o bloco de texto que
          os abrigava saiu do hero.

          Os tracinhos só aparecem com poucos quadros. Com os nove do
          celular eles viravam uma faixa de ~300px que lia como barra de
          rolagem — e, àquela largura, nenhum deles era alvo de toque
          confiável. Lá sobra o contador, e a troca se faz arrastando. */}
      <div className="absolute bottom-3.5 right-3.5 flex items-center gap-3 bg-[color-mix(in_srgb,var(--color-paper)_80%,transparent)] px-3.5 py-2 shadow-[0_6px_20px_-10px_rgba(0,0,0,.4)] backdrop-blur-[10px] md:bottom-5 md:right-6">
        {mostrarPontos && (
          <div className="flex gap-1.5" role="group" aria-label="Escolher foto">
            {slides.map((slide, i) => (
              <button
                key={slide.src}
                type="button"
                onClick={() => {
                  irPara(i);
                  parar();
                  agendar();
                }}
                aria-pressed={i === atual}
                aria-label={`Foto ${i + 1} de ${slides.length}`}
                className={`h-1 w-7 transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-on-surface ${
                  i === atual ? 'bg-[var(--hue-fill)]' : 'bg-outline-variant'
                }`}
              />
            ))}
          </div>
        )}
        <span className="type-data text-[11.5px] text-on-surface-variant">
          {atual + 1} / {slides.length}
        </span>
      </div>
    </header>
  );
};

export default HeroCarrossel;
